import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Typography,
  message,
  DatePicker,
  Space,
  Tabs,
} from 'antd';
import {
  PlusOutlined,
  FileExcelOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import WithdrawalServiceTable from '../component/withdrawal-service/WithdrawalServiceTable';
import WithdrawalServiceStatistics from '../component/withdrawal-service/WithdrawalServiceStatistics';
import CreateWithdrawalModal from '../component/withdrawal-service/CreateWithdrawalModal';
import WithdrawalPaymentModal from '../component/withdrawal-service/WithdrawalPaymentModal';
import WithdrawalMultiExportModal from '../component/withdrawal-service/WithdrawalMultiExportModal';
import EditWithdrawalModal from '../component/withdrawal-service/EditWithdrawalModal';
import TransactionService from '../service/transaction-service/Transaction.service';
import PaymentService from '../service/payment-service/Payment.service';
import useEmployee from '../hook/useEmployee';
import { useIsAdmin } from '../hook/useRole';
import CustomerService from '../service/customer-service/Customer.service';
import CustomerResponse from '../model/Customer.model';

const { Title, Text } = Typography;
const { MonthPicker } = DatePicker;
const { TabPane } = Tabs;

interface WithdrawalTransaction {
  id: number;
  transactionDate: string;
  createdAt?: string;
  amount: number;
  feeForCustomer?: number;
  customerType?: '%' | 'vn'; // Loại phí khách hàng: % hoặc VND
  feeApplied?: number;
  commissionRateForPartner?: number; // Phí CTV (%)
  balance: number;
  status: string;
  returnToCompany: boolean;
  employee: {
    id: number;
    name: string;
    contactInfo?: string;
  };
  posDevice?: {
    id: number;
    deviceCode: string;
    location: string;
  };
  creditCard?: {
    id: number;
    cardNumber: string;
    cardHolderName?: string;
    customer?: { name?: string };
  };
  paymentInfo?: {
    totalPaid: number;
    paymentCount: number;
    remainingAmount: number;
    totalAmount: number;
  };
}

const WithdrawalServicePage: React.FC = () => {
  // Raw data từ API (toàn bộ data trong tháng)
  const [allTransactions, setAllTransactions] = useState<
    WithdrawalTransaction[]
  >([]);
  const [allDeletedTransactions, setAllDeletedTransactions] = useState<
    WithdrawalTransaction[]
  >([]);

  // Filtered data sau khi apply filters
  const [filteredTransactions, setFilteredTransactions] = useState<
    WithdrawalTransaction[]
  >([]);
  const [filteredDeletedTransactions, setFilteredDeletedTransactions] =
    useState<WithdrawalTransaction[]>([]);

  const [loading, setLoading] = useState(false);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  // Pagination state (chỉ cho frontend pagination)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [deletedPagination, setDeletedPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [activeTab, setActiveTab] = useState('active');
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openMultiExportModal, setOpenMultiExportModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<
    WithdrawalTransaction | any
  >(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs()); // Current month by default
  const { employee } = useEmployee();
  const isAdmin = useIsAdmin();
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);

  useEffect(() => {
    CustomerService.getCustomerList().then(setCustomers);
  }, []);

  function enrichTransactionsWithPartnerName(
    transactions: any[],
    customers: CustomerResponse[]
  ) {
    return transactions.map((tx) => {
      const customerId = tx.creditCard?.customer?.id;
      const customer = customers.find((c) => c.id === customerId);
      return {
        ...tx,
        partnerName: customer?.partnerName || 'N/A',
      };
    });
  }

  // Current page data for display
  const currentPageTransactions = useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredTransactions.slice(start, end);
  }, [filteredTransactions, pagination.current, pagination.pageSize]);

  const currentPageDeletedTransactions = useMemo(() => {
    const start = (deletedPagination.current - 1) * deletedPagination.pageSize;
    const end = start + deletedPagination.pageSize;
    return filteredDeletedTransactions.slice(start, end);
  }, [
    filteredDeletedTransactions,
    deletedPagination.current,
    deletedPagination.pageSize,
  ]);

  // Load payment info for transactions
  const loadPaymentInfoForTransactions = async (
    transactionList: WithdrawalTransaction[]
  ) => {
    const transactionsWithPaymentInfo = await Promise.all(
      transactionList.map(async (transaction) => {
        try {
          const payments =
            await PaymentService.getPaymentsByWithdrawalTransaction(
              transaction.id
            );

          // Calculate payment summary
          const totalPaid = payments
            .filter(
              (payment: any) =>
                payment.originalStatus === 'COMPLETED' && !payment.deletedAt
            )
            .reduce((total: number, payment: any) => total + payment.amount, 0);

          const paymentCount = payments.filter(
            (payment: any) => !payment.deletedAt
          ).length;

          // Calculate total amount (thành tiền)
          let totalAmount = 0;
          if (transaction.feeForCustomer) {
            if (transaction.customerType === 'vn') {
              totalAmount = transaction.feeForCustomer;
            } else {
              totalAmount =
                transaction.amount * (transaction.feeForCustomer / 100);
            }
          }

          const remainingAmount = totalAmount - totalPaid;

          return {
            ...transaction,
            paymentInfo: {
              totalPaid,
              paymentCount,
              remainingAmount,
              totalAmount,
            },
          };
        } catch (error) {
          console.error(
            `Error loading payment info for transaction ${transaction.id}:`,
            error
          );
          return {
            ...transaction,
            paymentInfo: {
              totalPaid: 0,
              paymentCount: 0,
              remainingAmount: 0,
              totalAmount: 0,
            },
          };
        }
      })
    );

    return transactionsWithPaymentInfo;
  };

  // Fetch ALL transactions for the month (no pagination)
  const fetchAllTransactions = async (month = selectedMonth) => {
    if (!employee?.id) {
      console.warn('Employee not loaded yet, skipping fetch');
      return;
    }

    setLoading(true);
    try {
      const year = month.year();
      const monthNumber = month.month() + 1; // dayjs months are 0-indexed

      // Lấy TẤT CẢ giao dịch trong tháng
      const allTransactionsList =
        await TransactionService.getAllWithdrawalServiceTransactionsByMonth(
          year,
          monthNumber
        );

      const transactionsWithPaymentInfo =
        await loadPaymentInfoForTransactions(allTransactionsList);

      setAllTransactions(transactionsWithPaymentInfo);
      setFilteredTransactions(transactionsWithPaymentInfo); // Ban đầu không có filter
      setPagination((prev) => ({
        ...prev,
        current: 1,
        total: transactionsWithPaymentInfo.length,
      }));
    } catch (error) {
      console.error('Error fetching withdrawal transactions:', error);
      message.error('Không thể tải danh sách giao dịch rút tiền');
      setAllTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ALL deleted transactions for the month (no pagination)
  const fetchAllDeletedTransactions = async (month = selectedMonth) => {
    if (!employee?.id) {
      console.warn('Employee not loaded yet, skipping fetch');
      return;
    }

    setLoadingDeleted(true);
    try {
      const year = month.year();
      const monthNumber = month.month() + 1; // dayjs months are 0-indexed

      // Lấy TẤT CẢ giao dịch đã xóa trong tháng
      const allDeletedTransactionsList =
        await TransactionService.getAllDeletedWithdrawalServiceTransactionsByMonth(
          year,
          monthNumber
        );

      const deletedTransactionsWithPaymentInfo =
        await loadPaymentInfoForTransactions(allDeletedTransactionsList);

      setAllDeletedTransactions(deletedTransactionsWithPaymentInfo);
      setFilteredDeletedTransactions(deletedTransactionsWithPaymentInfo);
      setDeletedPagination((prev) => ({
        ...prev,
        current: 1,
        total: deletedTransactionsWithPaymentInfo.length,
      }));
    } catch (error) {
      console.error('Error fetching deleted withdrawal transactions:', error);
      message.error('Không thể tải danh sách giao dịch đã xóa');
      setAllDeletedTransactions([]);
      setFilteredDeletedTransactions([]);
    } finally {
      setLoadingDeleted(false);
    }
  };

  useEffect(() => {
    if (employee?.id) {
      if (activeTab === 'active') {
        fetchAllTransactions();
      } else if (activeTab === 'deleted') {
        fetchAllDeletedTransactions();
      }
    }
  }, [selectedMonth, employee?.id, activeTab]);

  // Handle filter changes from table
  const handleFilterChange = (filters: Record<string, any>) => {
    const sourceData =
      activeTab === 'active' ? allTransactions : allDeletedTransactions;
    let filtered = [...sourceData];

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return;

      switch (key) {
        case 'transactionDate':
          if (value && value[0] && value[1]) {
            const startDate = value[0].toDate();
            const endDate = value[1].toDate();
            filtered = filtered.filter((item) => {
              const itemDate = new Date(item.transactionDate);
              return itemDate >= startDate && itemDate <= endDate;
            });
          }
          break;

        case 'creditCard':
          const searchText = value[0]?.toLowerCase();
          if (searchText) {
            filtered = filtered.filter(
              (item) =>
                item.creditCard?.cardNumber
                  ?.toLowerCase()
                  .includes(searchText) ||
                item.creditCard?.cardHolderName
                  ?.toLowerCase()
                  .includes(searchText)
            );
          }
          break;

        case 'amount':
          const amountSearch = value[0];
          if (amountSearch) {
            filtered = filtered.filter((item) =>
              item.amount.toString().includes(amountSearch)
            );
          }
          break;

        case 'feeForCustomer':
          if (Array.isArray(value)) {
            filtered = filtered.filter((item) => {
              return value.some((filterValue) => {
                if (filterValue === 'vn') {
                  return (
                    item.customerType === 'vn' &&
                    item.feeForCustomer &&
                    item.feeForCustomer > 0
                  );
                } else if (filterValue === '%') {
                  return (
                    item.customerType === '%' &&
                    item.feeForCustomer &&
                    item.feeForCustomer > 0
                  );
                } else if (filterValue === 'none') {
                  return !item.feeForCustomer || item.feeForCustomer === 0;
                }
                return true;
              });
            });
          }
          break;

        case 'posDevice':
          if (Array.isArray(value) && value.length > 0) {
            filtered = filtered.filter(
              (item) =>
                item.posDevice && value.includes(item.posDevice.id.toString())
            );
          }
          break;

        case 'payment':
          if (Array.isArray(value)) {
            filtered = filtered.filter((item) => {
              return value.some((filterValue) => {
                const hasPaidPayment =
                  item.paymentInfo && item.paymentInfo.paymentCount > 0;
                if (filterValue === 'paid') {
                  return hasPaidPayment;
                } else if (filterValue === 'unpaid') {
                  return !hasPaidPayment;
                }
                return true;
              });
            });
          }
          break;

        case 'commissionRateForPartner':
          const commissionSearch = value[0];
          if (commissionSearch) {
            filtered = filtered.filter((item) =>
              item.commissionRateForPartner
                ?.toString()
                .includes(commissionSearch)
            );
          }
          break;

        case 'employee':
          const employeeSearch = value[0]?.toLowerCase();
          if (employeeSearch) {
            filtered = filtered.filter((item) =>
              item.employee.name.toLowerCase().includes(employeeSearch)
            );
          }
          break;
      }
    });

    // Update filtered data and reset pagination
    if (activeTab === 'active') {
      setFilteredTransactions(filtered);
      setPagination((prev) => ({
        ...prev,
        current: 1,
        total: filtered.length,
      }));
    } else {
      setFilteredDeletedTransactions(filtered);
      setDeletedPagination((prev) => ({
        ...prev,
        current: 1,
        total: filtered.length,
      }));
    }
  };

  const handleTableChange = (paginationInfo: any) => {
    if (activeTab === 'active') {
      setPagination((prev) => ({
        ...prev,
        current: paginationInfo.current,
        pageSize: paginationInfo.pageSize,
      }));
    } else if (activeTab === 'deleted') {
      setDeletedPagination((prev) => ({
        ...prev,
        current: paginationInfo.current,
        pageSize: paginationInfo.pageSize,
      }));
    }
  };

  const handleDeletedTableChange = (paginationInfo: any) => {
    setDeletedPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleCreateSuccess = () => {
    setOpenCreateModal(false);
    if (activeTab === 'active') {
      fetchAllTransactions();
    }
    message.success('Tạo giao dịch rút tiền thành công!');
  };

  const handleToggleStatus = async (transactionId: number) => {
    try {
      await TransactionService.toggleWithdrawalStatus(transactionId);
      message.success('Cập nhật trạng thái giao dịch thành công!');
      if (activeTab === 'active') {
        fetchAllTransactions();
      } else if (activeTab === 'deleted') {
        fetchAllDeletedTransactions();
      }
    } catch (error) {
      console.error('Error toggling transaction status:', error);
      message.error('Không thể cập nhật trạng thái giao dịch');
    }
  };

  const handleMonthChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setSelectedMonth(date);
      // Reset pagination when changing month
      setPagination((prev) => ({ ...prev, current: 1 }));
      setDeletedPagination((prev) => ({ ...prev, current: 1 }));
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    // Reset pagination when changing tab
    if (key === 'active') {
      setPagination((prev) => ({ ...prev, current: 1 }));
    } else if (key === 'deleted') {
      setDeletedPagination((prev) => ({ ...prev, current: 1 }));
    }
  };

  const handlePayment = (transaction: WithdrawalTransaction) => {
    setSelectedTransaction(transaction);
    setOpenPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setOpenPaymentModal(false);
    setSelectedTransaction(null);
    if (activeTab === 'active') {
      fetchAllTransactions();
    } else if (activeTab === 'deleted') {
      fetchAllDeletedTransactions();
    }
    message.success('Ghi nhận thanh toán thành công!');
  };

  const handleDelete = async (transactionId: number) => {
    try {
      await TransactionService.softDeleteTransaction(transactionId, employee?.id as number);
      message.success('Đã xóa giao dịch');
      // Refresh current active transactions and update deleted transactions
      if (activeTab === 'active') {
        fetchAllTransactions();
      }
      // Also refresh deleted transactions count if we're viewing deleted tab
      fetchAllDeletedTransactions();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      message.error('Không thể xóa giao dịch');
    }
  };

  // Show loading if employee is not loaded yet
  if (!employee) {
    return (
      <div className="p-6">
        <Card className="text-center py-12">
          <Text>Đang tải thông tin nhân viên...</Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <Card className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <Title level={2} className="mb-2">
              Dịch vụ rút tiền
            </Title>
            <Text type="secondary" className="text-base">
              Quản lý giao dịch rút tiền từ thẻ tín dụng với POS tối ưu
            </Text>
          </div>
          <Space>
            <Button
              type="default"
              icon={<FileExcelOutlined />}
              onClick={() => setOpenMultiExportModal(true)}
              size="large"
              className="bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700"
              disabled={filteredTransactions.length === 0}
            >
              Xuất Excel nhiều GD
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setOpenCreateModal(true)}
              className="bg-blue-500 hover:bg-blue-600"
            >
              Tạo giao dịch rút tiền
            </Button>
          </Space>
        </div>
      </Card>

      {/* Month Filter */}
      <Card className="mb-6">
        <div className="flex items-center space-x-4">
          <Text strong className="text-base">
            Lọc theo tháng:
          </Text>
          <MonthPicker
            value={selectedMonth}
            onChange={handleMonthChange}
            format="MM/YYYY"
            placeholder="Chọn tháng"
            className="w-40"
          />
          <Text type="secondary">Tháng {selectedMonth.format('MM/YYYY')}</Text>
        </div>
      </Card>

      {/* Statistics */}
      <WithdrawalServiceStatistics
        transactions={
          activeTab === 'active'
            ? filteredTransactions
            : filteredDeletedTransactions
        }
        totalCount={
          activeTab === 'active' ? pagination.total : deletedPagination.total
        }
      />

      {/* Transactions Tabs */}
      <Card className="w-full">
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Giao dịch hoạt động" key="active">
            <WithdrawalServiceTable
              transactions={currentPageTransactions}
              loading={loading}
              pagination={pagination}
              onTableChange={handleTableChange}
              onToggleStatus={handleToggleStatus}
              onPayment={handlePayment}
              onDelete={handleDelete}
              onEdit={(transaction) => {
                // Convert WithdrawalTransaction to TransactionResponse for modal
                const transactionResponse: any = {
                  ...transaction,
                  transactionType: 'withdrawal',
                  transactionFor: 'withdrawal',
                };
                setSelectedTransaction(transactionResponse);
                setOpenEditModal(true);
              }}
              onPayCustomer={(transaction) => {
                console.log('Pay customer for transaction:', transaction);
                // Reload transactions after payment
                fetchAllTransactions();
              }}
              onFilterChange={handleFilterChange}
              isAdmin={isAdmin}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                <DeleteOutlined />
                <span className="ml-1">Giao dịch đã xóa</span>
              </span>
            }
            key="deleted"
          >
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 font-medium">
                📋 Đây là danh sách các giao dịch đã bị xóa. Một số thao tác có
                thể bị hạn chế.
              </p>
            </div>
            <WithdrawalServiceTable
              transactions={currentPageDeletedTransactions}
              loading={loadingDeleted}
              pagination={deletedPagination}
              onTableChange={handleDeletedTableChange}
              onToggleStatus={handleToggleStatus}
              onPayment={handlePayment}
              onDelete={handleDelete}
              onEdit={(transaction) => {
                // Convert WithdrawalTransaction to TransactionResponse for modal
                const transactionResponse: any = {
                  ...transaction,
                  transactionType: 'withdrawal',
                  transactionFor: 'withdrawal',
                };
                setSelectedTransaction(transactionResponse);
                setOpenEditModal(true);
              }}
              onPayCustomer={(transaction) => {
                console.log('Pay customer for transaction:', transaction);
                // Reload deleted transactions after payment
                fetchAllDeletedTransactions();
              }}
              onFilterChange={handleFilterChange}
              isAdmin={isAdmin}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Create Transaction Modal */}
      <CreateWithdrawalModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Payment Modal */}
      <WithdrawalPaymentModal
        visible={openPaymentModal}
        onCancel={() => {
          setOpenPaymentModal(false);
          setSelectedTransaction(null);
        }}
        onSuccess={handlePaymentSuccess}
        transaction={selectedTransaction}
      />

      {/* Multi Export Modal */}
      <WithdrawalMultiExportModal
        visible={openMultiExportModal}
        onClose={() => setOpenMultiExportModal(false)}
        transactions={enrichTransactionsWithPartnerName(
          filteredTransactions,
          customers
        )}
        selectedMonth={selectedMonth.month() + 1}
        selectedYear={selectedMonth.year()}
      />

      {/* Edit Transaction Modal */}
      {selectedTransaction && openEditModal && (
        <EditWithdrawalModal
          open={openEditModal}
          onClose={() => {
            setOpenEditModal(false);
            setSelectedTransaction(null);
          }}
          onSuccess={() => {
            setOpenEditModal(false);
            setSelectedTransaction(null);
            if (activeTab === 'active') {
              fetchAllTransactions();
            } else if (activeTab === 'deleted') {
              fetchAllDeletedTransactions();
            }
            message.success('Cập nhật giao dịch thành công!');
          }}
          transaction={selectedTransaction}
        />
      )}
    </div>
  );
};

export default WithdrawalServicePage;
